/**
 * useCardAnimation — per-entity Animated.Values that spring to each card's
 * target slot, so the cluster reflows smoothly when cards are added/removed
 * (KEYSTONE-1256 step 2).
 *
 * Uses RN's built-in Animated with useNativeDriver: true — all animated props
 * are transforms (translateX/Y, rotate, scale), so the springs run on the UI
 * thread off the JS bridge (no reanimated dependency).
 *
 * Usage: each render, build the list of card targets and pass them to
 * `sync(targets)` from an effect; read each card's style with `getStyle(id)`
 * during render. Animated values persist across renders (keyed by id); a card
 * seen for the first time spawns at `spawnX` (the section center) at scale 0 and
 * springs out to its target.
 */

import { useEffect, useReducer, useRef } from "react";
import { Animated } from "react-native";

/** Prototype spring config (DEFAULT_CONFIG: stiffness 260, damping 22, mass 1). */
const SPRING = { stiffness: 260, damping: 22, mass: 1, useNativeDriver: true as const };
const SPAWN_SCALE = 0; // prototype spawnScale

export interface CardTarget {
  id: string;
  x: number;        // translateX (px, card center relative to stage center)
  y: number;        // translateY (px)
  rotation: number; // deg
  scale: number;    // relative to base card size
  /** Entry X for a newly-seen card (prototype "muse" spawn → 0 = stage center). */
  spawnX: number;
}

interface CardAnim {
  x: Animated.Value;
  y: Animated.Value;
  rotation: Animated.Value;
  scale: Animated.Value;
  /** Rotation interpolated to a deg string once (reused by getStyle). */
  rotate: Animated.AnimatedInterpolation<string>;
  /** Last target applied to this card, so we only re-spring when it moves. */
  last?: { x: number; y: number; rotation: number; scale: number };
}

function stop(a: CardAnim): void {
  a.x.stopAnimation();
  a.y.stopAnimation();
  a.rotation.stopAnimation();
  a.scale.stopAnimation();
}

export interface AnimatedCardStyle {
  transform: (
    | { translateX: Animated.Value }
    | { translateY: Animated.Value }
    | { rotate: Animated.AnimatedInterpolation<string> }
    | { scale: Animated.Value }
  )[];
}

export function useCardAnimation() {
  const anims = useRef(new Map<string, CardAnim>()).current;
  // sync() runs in an effect and only mutates the anims ref, so getStyle reads
  // null for any card created this pass until a render happens. On a fresh mount
  // with a settled store (e.g. returning from the gallery) no further render is
  // coming, so we force one when sync first populates a card's animation.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  // Stop every running spring when the owner unmounts so no native animation
  // outlives the component.
  useEffect(() => () => { anims.forEach(stop); anims.clear(); }, [anims]);

  function ensure(id: string, spawnX: number): CardAnim {
    let a = anims.get(id);
    if (!a) {
      const rotation = new Animated.Value(0);
      a = {
        x: new Animated.Value(spawnX),
        y: new Animated.Value(0),
        rotation,
        scale: new Animated.Value(SPAWN_SCALE),
        rotate: rotation.interpolate({
          inputRange: [-360, 360],
          outputRange: ["-360deg", "360deg"],
        }),
      };
      anims.set(id, a);
    }
    return a;
  }

  /**
   * Spring cards to their targets. New cards are created at their spawn point
   * first (so they animate in). Only cards whose target actually changed are
   * re-sprung. Animated values for ids not in `targets` are stopped and dropped.
   * Call from an effect keyed on the targets.
   */
  function sync(targets: CardTarget[]) {
    const present = new Set<string>();
    const animations: Animated.CompositeAnimation[] = [];
    let created = false;
    for (const t of targets) {
      present.add(t.id);
      if (!anims.has(t.id)) created = true;
      const a = ensure(t.id, t.spawnX);
      const moved =
        !a.last ||
        a.last.x !== t.x ||
        a.last.y !== t.y ||
        a.last.rotation !== t.rotation ||
        a.last.scale !== t.scale;
      if (!moved) continue;
      a.last = { x: t.x, y: t.y, rotation: t.rotation, scale: t.scale };
      animations.push(
        Animated.spring(a.x, { toValue: t.x, ...SPRING }),
        Animated.spring(a.y, { toValue: t.y, ...SPRING }),
        Animated.spring(a.rotation, { toValue: t.rotation, ...SPRING }),
        Animated.spring(a.scale, { toValue: t.scale, ...SPRING }),
      );
    }
    // Stop and drop cards that are no longer present so their springs don't
    // keep running on the UI thread after the card unmounts.
    for (const [id, a] of anims) {
      if (!present.has(id)) {
        stop(a);
        anims.delete(id);
      }
    }
    if (animations.length > 0) Animated.parallel(animations).start();
    // A card created this pass has no style until the next render; trigger one
    // so getStyle returns its transform and the card actually mounts.
    if (created) forceRender();
  }

  /** Read the animated transform style for a card (pure; safe in render). */
  function getStyle(id: string): AnimatedCardStyle | null {
    const a = anims.get(id);
    if (!a) return null;
    return {
      transform: [
        { translateX: a.x },
        { translateY: a.y },
        { rotate: a.rotate },
        { scale: a.scale },
      ],
    };
  }

  return { sync, getStyle };
}

/** Stable signature of a target list, to gate the spring effect. */
export function targetsKey(targets: CardTarget[]): string {
  return targets
    .map((t) => `${t.id}:${Math.round(t.x)},${Math.round(t.y)},${Math.round(t.rotation)},${t.scale.toFixed(3)}`)
    .join("|");
}
